import { useCallback, useState } from 'react'

import { PostNotPublishableReason } from 'models/socialCampaign'
import constate from 'constate'

/**
 * The one dismissible message under the campaign's header after an action (prototype v3 `st.flash`).
 * The tab renders it; any part of the tab shows one: the lifecycle actions here, the launch and the
 * generation of the schedule's action bar (ISSUE-3623).
 */
export type CampaignFlash =
  /** Every draft scheduled: "7 posts scheduled. 1 more will be written when your order arrives." */
  | { kind: 'launched', scheduled: number, waiting: number }
  /** "Generate new posts" answered: "4 new posts are ready to review…" */
  | { kind: 'generated', count: number }
  /** A launch refused (422): every post that cannot go out as it is, by its subject, and why */
  | { kind: 'not_publishable', posts: { subject: string, reasons: PostNotPublishableReason[] }[] }
  /** "The campaign is running again." and the posts missed during the pause */
  | { kind: 'resumed', missed: number }
  | { kind: 'reopened' }
  /** An action the backend refused or that failed: the page shows the campaign as it is */
  | { kind: 'error' }

export const [CampaignFlashProvider, useCampaignFlash] = constate(() => {
  const [flash, setFlash] = useState<CampaignFlash | null>(null)

  const showFlash = useCallback((next: CampaignFlash) => setFlash(next), [])
  const dismissFlash = useCallback(() => setFlash(null), [])

  return { flash, showFlash, dismissFlash }
})
